import { Typography, IconButton, Box, SvgIcon, Stack } from '@mui/material';
import React, { useContext, useState } from 'react';
import { borderRadius, palette, spaces } from '../../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../../utils/getMuiIcon';
import { ConversationListItem } from '@kimuno/api-sdk/lib/types/responses/Conversation';
import useScreenSize from '../../../../utils/useScreenSize';
import { useTranslation } from 'react-i18next';
import useAction from '../../../../utils/useAction';
import {
  getConversation as getConversationAction,
  setCurrentConversationId as setCurrentConversationIdAction,
  deleteConversation as deleteConversationAction
} from '../../../../actions/aiAssistant.actions';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import services from '../../../../services';
import { ActiveView } from '../../shared/constants';
import useRouter from '../../../../utils/useRouter';
import { aiAssistantPageSource, routes } from '../../../../constants';
import { getAIAssistantPageSource } from '../../../../utils/getAIAssistantPageSource';
import ConfirmationDialog from '../../../Dialog/ConfirmationDialog';

const ICON_CONTAINER_SIZE = 24;
const ICON_SIZE = 16;

const getIconContainerStyles = (isHovered: boolean) => ({
  width: ICON_CONTAINER_SIZE,
  height: ICON_CONTAINER_SIZE,
  color: isHovered ? palette.primary.main : palette.text.secondary,
  display: 'flex',
  flexShrink: 0,
});

const iconStyles = {
  width: ICON_SIZE,
  height: ICON_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const getTypographyStyles = (isHovered: boolean) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  flex: 1,
  marginLeft: spaces.sm,
  marginRight: isHovered ? spaces.xs : 0,
  color: palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1,
});

const deleteConfirmationMyContentsKey =
  'By confirming, the chat and all content and media created within it '
  + '(e.g. documents, images and podcasts) will be permanently deleted '
  + 'and will no longer be available in \'My Content\'.';

const AIAssistantChatHistoryListItem = ({ data }: { data: ConversationListItem }) => {
  const { t } = useTranslation();
  const { router, match } = useRouter();

  const [isHovered, setIsHovered] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const { setActiveView } = useContext(aiAssistantContext);

  const setCurrentConversationId = useAction(setCurrentConversationIdAction);
  const fetchConversation = useAction(getConversationAction);
  const deleteConversation = useAction(deleteConversationAction);

  const isOnHistoryPage = match.location.pathname.includes(`/${routes.aiAssistantHistory.root}`);

  const deleteDialogTitle = t(
    'This chat will be deleted permanently: {{title}}',
    { title: data.title }
  );
  const deleteDialogMessage = t(deleteConfirmationMyContentsKey);

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleClick = async () => {
    // If on history page, navigate to AI Tools page to show the chat
    if (isOnHistoryPage) {
      router.push(`/${routes.aiTools.root}/${data.id}`);
    } else {
      await fetchConversation({ conversationId: data.id });
      setCurrentConversationId({ conversationId: data.id });
      setActiveView(ActiveView.CHAT);
    }
    
    const pageSource = isOnHistoryPage
      ? aiAssistantPageSource.chatHistory
      : getAIAssistantPageSource(match.location.pathname);

    services.track.ampliTrackAIAssistantPastChatOpenedEvent({
      conversationId: data.id,
      pageSource,
    });
  };

  const handleDeleteConfirm = () => {
    deleteConversation({ conversationId: data.id });
    setCurrentConversationId({ conversationId: null });
    setShowDeleteDialog(false);
  };

  return  (
    <>
      <Box 
        key={data.id}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        sx={{
          '&:hover': {
            backgroundColor: isOnHistoryPage ? palette.action.hover : palette.primary.selected,
            borderRadius: borderRadius.sm,
          },
          cursor: 'pointer',
          paddingX: spaces.sm,
          paddingY: spaces.xxs,
        }}
        data-testid='ai-assistant-chat-history-list-item'
      >
        <Box sx={{ 
          width: '100%', 
          paddingY: 0,
          paddingRight: isHovered ? spaces.xs : spaces.md
        }}>
          <Stack 
            direction='row'
            alignItems='center' 
            justifyContent='space-between'
            sx={{
              width: '100%',
              minHeight: '40px' //size of the trash icon
            }}
          >
            <Stack 
              direction='row'
              alignItems='center'
              justifyContent='center'
              sx={getIconContainerStyles(isHovered)}
            >
              <SvgIcon
                sx={iconStyles}
                viewBox='0 0 24 24'
                component={getMuiIcon('aiChatsOld')}
              /> 
            </Stack>
            <Typography 
              variant='menu.itemDense' 
              sx={getTypographyStyles(isHovered)}
            >
              {data.title}
            </Typography>
            {isDesktopScreen && isHovered &&  (
              <IconButton
                data-testid='delete-conversation-button'
                size='medium'
                onClick={handleDelete}
                sx={{
                  opacity: 0.7,
                  padding: spaces.xxs,
                  color: palette.text.secondary,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    color: palette.primary.main
                  }
                }}
              >
                <SvgIcon
                  sx={{ width: 24, height: 24 }}
                  viewBox='0 0 24 24'
                  component={getMuiIcon('trash')}
                /> 
              </IconButton>
            )}
          </Stack>
        </Box>
      </Box>
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        title={deleteDialogTitle}
        message={deleteDialogMessage}
        onConfirm={handleDeleteConfirm}
        onCloseModal={() => setShowDeleteDialog(false)}
        confirmButtonText={t('Yes, delete')}
        confirmButtonColor='error'
        dialogSx={{ zIndex: 1500 }}
      />
    </>
  );
};

export default AIAssistantChatHistoryListItem;
